import { requalifyForecastProperty } from "components/groupBy/helpers/qualifyForecastProperty";

/** The subset of the favourite-columns response a focus tile is built from. */
export interface FavouriteColumn {
  property: string;
  group?: string;
  forecastFolder?: string;
  rescat?: string;
  isForecastToggleOn?: boolean;
  forecastFolderExplicit?: boolean;
}

// The two groups whose properties can carry a forecast, spelled the same way the column
// labeller tests for one (getColumnLabelWithContext).
const FORECAST_CAPABLE_GROUPS: string[] = ["Performance", "Forecast"];

/**
 * Turns the org's default columns into focus tiles.
 *
 * A tile is one string, so a column that names its own forecast source has to carry that
 * source in the property. Columns without one pass through untouched and keep resolving
 * against the project default, which is also what facility columns do.
 *
 * The group is read as well as the two forecast fields, because these documents are edited by
 * hand: the same fields set on a header column would mint a property no forecast answers to,
 * and that tile would then attach forecast settings to a request that never asked for one.
 *
 * The rescat is required for the same reason, and it is a hand edit away: the suffix grammar
 * has no branch for a source without one. Trumping without a rescat writes the bare marker,
 * which reads back as EVA Research — the same silent source swap this mapper exists to stop,
 * running the other way. Any other source without one writes the literal string "undefined"
 * where the reserve category belongs, and that travels into the outgoing request. So a column
 * naming a source but no reserve category is passed through unqualified, where it resolves
 * against the project default, rather than half qualified.
 *
 * isForecastToggleOn is deliberately not read: the tile has nowhere to keep it, and the read
 * path derives it back from the forecast marker, so consuming it here would be a second
 * source of truth for the same fact.
 */
export const toFocusTileProperties = (columns: FavouriteColumn[]): string[] =>
  columns.map((c) =>
    c.forecastFolderExplicit &&
    c.forecastFolder &&
    c.rescat &&
    FORECAST_CAPABLE_GROUPS.includes(c.group ?? "")
      ? requalifyForecastProperty(c.property, c.forecastFolder, c.rescat)
      : c.property
  );
